import { palette, Box, HairlineCard, Stack, Typography, type LucideIcon } from '@munifocus/ui';
import type { ReactNode } from 'react';

/**
 * A titled settings section — hairline card, tinted icon, description, then its
 * controls.
 *
 * Lives here rather than inside `Settings.tsx` so sections that decide for
 * themselves whether to render at all (the passkey card hides on devices with no
 * authenticator) can own their whole card, header included, instead of leaving
 * an empty shell behind.
 */
export function SettingsSection({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <HairlineCard sx={{ p: { xs: 2.5, md: 3 } }}>
      <Stack direction="row" spacing={1.75} sx={{ alignItems: 'flex-start' }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: palette.brand.accentTint,
          }}
        >
          <Icon size={18} color={palette.brand.accent} />
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 13, color: palette.text.secondary }}>
            {description}
          </Typography>
          <Box sx={{ mt: 2 }}>{children}</Box>
        </Box>
      </Stack>
    </HairlineCard>
  );
}
